import type { OverviewCage } from '@/domain/client';
import type { SiteTimelineScope } from '@/domain/fishGroups';
import type { StockingCycle } from '@/domain/stockingCycles';

import type { EmptyPen } from '../types';

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

type SitePen = Pick<OverviewCage, 'cageId' | 'cageName'>;

/**
 * The pens of one site from the client overview. Asked for with fish groups, the overview
 * puts every pen without an active group in `emptyCages`, so the active and inactive
 * buckets only hold occupied pens and historical pens are decommissioned.
 */
export interface SitePens {
    emptyCages: SitePen[];
}

/**
 * Cycles a new group can go into: planned and active ones (D14, D15). Active first, then
 * planned by their planned start.
 */
export function selectableCycles<T extends StockingCycle>(cycles: T[]): T[] {
    const rank = (cycle: StockingCycle) => (cycle.status === 'active' ? 0 : 1);
    return cycles
        .filter((cycle) => cycle.status !== 'completed')
        .sort(
            (a, b) =>
                rank(a) - rank(b) ||
                (a.plannedStartDate ?? '').localeCompare(b.plannedStartDate ?? '') ||
                collator.compare(a.name, b.name)
        );
}

/** When a completed cycle ended: its last close, else its last stocking, else its creation. */
function endedAt(cycle: StockingCycle): number {
    return (cycle.lastClosedAt ?? cycle.stockedTo ?? cycle.createdAt).getTime();
}

/**
 * Every cycle the board can show: the selectable ones, then the completed ones, latest first.
 * A completed stocking stays reachable, read-only, once its last group has closed.
 */
export function boardCycles<T extends StockingCycle>(cycles: T[]): T[] {
    const completed = cycles
        .filter((cycle) => cycle.status === 'completed')
        .sort((a, b) => endedAt(b) - endedAt(a) || collator.compare(b.name, a.name));
    return [...selectableCycles(cycles), ...completed];
}

/**
 * Where the board opens: the active cycle, else the first planned one, else the latest
 * completed one, else the groups in no cycle. Null only for a site with none of those.
 */
export function defaultScope(
    cycles: StockingCycle[],
    unassignedCount: number
): SiteTimelineScope | null {
    const first = boardCycles(cycles)[0];
    if (first) return first.id;
    return unassignedCount > 0 ? 'unassigned' : null;
}

/**
 * What the board shows: the customer's pick while it is still offered (the unassigned groups
 * can all be assigned), else the default scope.
 */
export function resolveScope(
    picked: SiteTimelineScope | null,
    cycles: StockingCycle[],
    unassignedCount: number
): SiteTimelineScope | null {
    if (picked === 'unassigned' && unassignedCount > 0) return picked;
    if (typeof picked === 'number' && cycles.some((c) => c.id === picked)) return picked;
    return defaultScope(cycles, unassignedCount);
}

/**
 * Pens a new group can go into: the overview's empty pens, less `occupiedCageIds`, the pens
 * that any live group of the site holds. That includes a stocking registered for a later date
 * in another cycle, which the overview doesn't count as active. The backend still enforces
 * emptiness, so a race only costs a TARGET_CAGE_NOT_EMPTY message.
 *
 * Takes the real overview: `cageName` stays real, and only `displayName` goes through
 * `penDisplayName` (demo masking).
 */
export function emptyPens(
    location: SitePens | null,
    occupiedCageIds: ReadonlySet<number>,
    penDisplayName: (cageId: number, cageName: string) => string = (_id, name) => name
): EmptyPen[] {
    if (!location) return [];

    return location.emptyCages
        .filter((cage) => !occupiedCageIds.has(cage.cageId))
        .sort((a, b) => collator.compare(a.cageName, b.cageName))
        .map((cage) => ({
            cageId: cage.cageId,
            cageName: cage.cageName,
            displayName: penDisplayName(cage.cageId, cage.cageName),
        }));
}
